class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close({ restoreFocus: true }));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    /* Body scroll lock. `body { overflow: hidden }` alone - which is all the
       .overflow-hidden class does - is not honoured by iOS Safari, so a touch
       that starts anywhere other than the scrolling list drags the page behind
       the drawer instead.

       Same pattern ml-stone-selector.js already uses: a non-passive listener on
       document, armed only while the drawer is open, that cancels the gesture
       unless it began inside the one element that is allowed to scroll. A
       permanent non-passive listener would defeat the browser's scroll
       optimisation for the whole site, so it is added on open and removed on
       close.

       The node is looked up per event, never cached: renderContents() replaces
       the drawer's innerHTML on every cart update, so a cached reference would
       point at a detached node after the first add-to-cart.

       overscroll-behavior: contain on the same element (component-cart-drawer.css)
       handles the other half - chaining once the list is scrolled to its end. */
    this.scrollGuard = (event) => {
      const target = event.target;
      if (target && target.closest && target.closest('.cart-drawer-items-and-upsell')) return;
      event.preventDefault();
    };
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    document.addEventListener('touchmove', this.scrollGuard, { passive: false });
    document.addEventListener('wheel', this.scrollGuard, { passive: false });
  }

  /* Returning focus to the trigger is correct for keyboard and wrong for pointer.
     #cart-icon-bubble carries .focus-inset and base.css sizes .header__icon to
     4.4rem, so a programmatic focus() paints a 44x44 ring around a 20x20 icon
     AFTER the drawer has gone. That is the box users see on mobile.

     Only Escape passes { restoreFocus: true }. The overlay listener is bound
     directly so it passes a MouseEvent, and the two close buttons pass nothing;
     both read as falsy here, which is what we want. removeTrapFocus still runs
     either way, so the document listeners always detach. */
  close(opts) {
    this.classList.remove('active');
    const restoreFocus = !!(opts && opts.restoreFocus === true);
    removeTrapFocus(restoreFocus ? this.activeElement : null);
    if (!restoreFocus && this.activeElement) {
      // The focus-visible polyfill in global.js may have added this already.
      this.activeElement.classList.remove('focused');
    }
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('touchmove', this.scrollGuard);
    document.removeEventListener('wheel', this.scrollGuard);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
